import * as React from 'react';
import * as I18n from 'i18n-js';
import * as guitarExampleIcon from '../../../images/icons/category-icons/electric-guitars.svg';
import * as accessoriesExampleIcon from '../../../images/icons/category-icons/accessories.svg';
import { CURRENCY_DESCRIPTION } from '@reverbdotcom/commons/dist/currency_descriptions';
import { formatNumber } from '@reverbdotcom/commons/dist/money';
import AlertBox from '@reverbdotcom/commons/dist/components/alert_box';

const GUITAR = 'guitar';
const ACCESSORY = 'accessory';

interface IExampleItemProps {
  title: string;
  type: 'guitar' | 'accessory';
  combinedShippingPrice?: string;
  currency: string;
}

interface IExamplesProps {
  currency: string;
}

function formattedPrice(price: number, currency: string) {
  const { symbol, displayCode } = CURRENCY_DESCRIPTION[currency];
  let modifiedPrice = price;

  if (currency === 'JPY') {
    modifiedPrice = price * 100;
  }

  return `${symbol}${formatNumber(modifiedPrice)} ${displayCode}`;
}

function ExampleItem({ type, combinedShippingPrice, currency, title }: IExampleItemProps) {
  return (
    <div className="d-flex">
      <div className="mr-1" style={{ width: '6rem' }}>
        {type === GUITAR &&
          <img
            className="d-block width-100 height-auto"
            src={guitarExampleIcon}
            alt={I18n.t('discovery.dashboard.selling.shippingRates.examples.guitarIcon')}
          />
        }
        {type === ACCESSORY &&
          <img
            className="d-block width-100 height-auto"
            src={accessoriesExampleIcon}
            alt={I18n.t('discovery.dashboard.selling.shippingRates.examples.accessoriesIcon')}
          />
        }
      </div>
      <div className="ml-1">
        <p className="size-90 weight-bold lh-120 mb-half">
          {title}
        </p>
        <p className="size-90 lh-120 mb-0">
          {type === GUITAR && formattedPrice(1000, currency)}
          {type === ACCESSORY && formattedPrice(10, currency)}
          <br />
          {type === GUITAR && I18n.t('discovery.dashboard.selling.shippingRates.examples.shippingCost', { price: formattedPrice(75, currency) })}
          {type === ACCESSORY && (
            combinedShippingPrice ? (
              <>
                <span className="td-line-through">
                  {I18n.t('discovery.dashboard.selling.shippingRates.examples.shippingCost', { price: formattedPrice(5, currency) })}
                </span>
                <br />
                <span className="color-green weight-bold">
                  {I18n.t('discovery.dashboard.selling.shippingRates.examples.combinedShippingCost', { price: combinedShippingPrice })}
                </span>
              </>
            ) : (
              I18n.t('discovery.dashboard.selling.shippingRates.examples.shippingCost', { price: formattedPrice(5, currency) })
            )
          )}
        </p>
      </div>
    </div>
  );
}

export default function CombinedShippingRateExamples(props: IExamplesProps) {
  const [visible, toggleVisibility] = React.useState(false);

  return (
    <AlertBox color="blue">
      <h3 className="weight-bold">
        {I18n.t('discovery.dashboard.selling.shippingRates.examples.understandingCombinedShipping')}
      </h3>
      <p className="scaling-mb-2 opacity-80 size-90">
        {I18n.t('discovery.dashboard.selling.shippingRates.examples.hereAreTwoExamples')}
      </p>
      <button
        type="button"
        onClick={() => toggleVisibility(!visible)}
        className="button-as-link"
      >
        {visible ? (
          <span className="icon-r-angle-up">
            {I18n.t('discovery.dashboard.selling.shippingRates.examples.hideExamples')}
          </span>
        ) : (
          <span className="icon-r-angle-down">
            {I18n.t('discovery.dashboard.selling.shippingRates.examples.showExamples')}
          </span>
        )}
      </button>
      {visible &&
        <>
          <div className="scaling-mt-2 bg-white bd-1 bd--offwhite bd-rounded scaling-padding-2">
            <p className="weight-bold scaling-mb-2">
              {I18n.t('discovery.dashboard.selling.shippingRates.examples.exampleOneDifferentItems')}
            </p>
            <div className="g-container">
              <div className="g-col-4 g-col-mobile-12 mobile-mb-1">
                <ExampleItem
                  title={I18n.t('discovery.dashboard.selling.shippingRates.examples.guitar')}
                  type={GUITAR}
                  currency={props.currency}
                />
              </div>
              <div className="g-col-4 g-col-mobile-12">
                <ExampleItem
                  title={I18n.t('discovery.dashboard.selling.shippingRates.examples.guitarCable')}
                  type={ACCESSORY}
                  currency={props.currency}
                  combinedShippingPrice={formattedPrice(1, props.currency)}
                />
              </div>
            </div>
            <p className="size-90 scaling-mt-2 mb-0 weight-bold">
              {I18n.t('discovery.dashboard.selling.shippingRates.examples.checkoutTotal', { total: formattedPrice(1010, props.currency), shippingTotal: formattedPrice(76, props.currency) })}
            </p>
            <p className="size-90 mb-0 opacity-80">
              {I18n.t('discovery.dashboard.selling.shippingRates.examples.exampleOneBuyerPays', { combinedRate: formattedPrice(1, props.currency) })}
            </p>
          </div>
          <div className="scaling-mt-2 bg-white bd-1 bd--offwhite bd-rounded scaling-padding-2">
            <p className="weight-bold scaling-mb-2">
              {I18n.t('discovery.dashboard.selling.shippingRates.examples.exampleTwoSameItems')}
            </p>
            <div className="g-container">
              <div className="g-col-4 g-col-mobile-12 mobile-mb-1">
                <ExampleItem
                  title={`${I18n.t('discovery.dashboard.selling.shippingRates.examples.guitarCable')} 1`}
                  type={ACCESSORY}
                  currency={props.currency}
                />
              </div>
              <div className="g-col-4 g-col-mobile-12 mobile-mb-1">
                <ExampleItem
                  title={`${I18n.t('discovery.dashboard.selling.shippingRates.examples.guitarCable')} 2`}
                  type={ACCESSORY}
                  currency={props.currency}
                  combinedShippingPrice={formattedPrice(1, props.currency)}
                />
              </div>
              <div className="g-col-4 g-col-mobile-12">
                <ExampleItem
                  title={`${I18n.t('discovery.dashboard.selling.shippingRates.examples.guitarCable')} 3`}
                  type={ACCESSORY}
                  currency={props.currency}
                  combinedShippingPrice={formattedPrice(1, props.currency)}
                />
              </div>
            </div>
            <p className="size-90 scaling-mt-2 mb-0 weight-bold">
              {I18n.t('discovery.dashboard.selling.shippingRates.examples.checkoutTotal', { total: formattedPrice(30, props.currency), shippingTotal: formattedPrice(7, props.currency) })}
            </p>
            <p className="size-90 mb-0 opacity-80">
              {I18n.t('discovery.dashboard.selling.shippingRates.examples.exampleTwoBuyerPays', { combinedRate: formattedPrice(1, props.currency) })}
            </p>
          </div>
        </>
      }
    </AlertBox>
  );
}



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/components/shipping_profiles/combined_shipping_rate_examples.tsx